import { create } from 'zustand'

// Pricing V3: only two plans exist server-side.
// Server is the source of truth for both vehicleLimit and the features list —
// the store mirrors what `/v1/entitlements/me` returns and never derives gating
// from a hardcoded client table.
export const PLANS = {
  FREE: 'PERSONAL_FREE',
  PRO: 'PERSONAL_PRO',
}

const PLAN_DISPLAY = {
  [PLANS.FREE]: { name: 'Free', color: '#10b981' },
  [PLANS.PRO]: { name: 'Pro', color: '#f97316' },
}

export const useSubscriptionStore = create((set, get) => ({
  // Mirror of GET /v1/entitlements/me
  effectivePlan: PLANS.FREE,
  vehicleLimit: 1,
  features: [],
  trialEligible: false,
  trialDaysRemaining: null,
  status: null,
  billing: null,
  validFrom: null,
  validTo: null,
  trialEndsAt: null,
  hasUsedTrial: false,
  planKey: null,
  isActive: false,
  entitlementLoaded: false,

  // Local-only — vehicle service tells us how many we currently have.
  vehicleCount: 0,

  loadFromBackend: (data) => {
    if (!data) {
      set({
        effectivePlan: PLANS.FREE,
        vehicleLimit: 1,
        features: [],
        trialEligible: false,
        trialDaysRemaining: null,
        status: null,
        billing: null,
        validFrom: null,
        validTo: null,
        trialEndsAt: null,
        hasUsedTrial: false,
        planKey: null,
        isActive: false,
        entitlementLoaded: true,
      })
      return
    }
    set({
      effectivePlan: data.effectivePlan || PLANS.FREE,
      vehicleLimit: typeof data.vehicleLimit === 'number' ? data.vehicleLimit : 1,
      features: Array.isArray(data.features) ? data.features : [],
      trialEligible: !!data.trialEligible,
      trialDaysRemaining:
        typeof data.trialDaysRemaining === 'number' ? data.trialDaysRemaining : null,
      status: data.status || null,
      billing: data.billing || null,
      validFrom: data.validFrom || null,
      validTo: data.validTo || null,
      trialEndsAt: data.trialEndsAt || null,
      hasUsedTrial: !!data.hasUsedTrial,
      planKey: data.planKey || null,
      isActive: !!data.isActive,
      entitlementLoaded: true,
    })
  },

  setVehicleCount: (count) => set({ vehicleCount: count }),

  // Convenience getters (server-authoritative)
  hasFeature: (feature) => get().features.includes(feature),
  isPro: () => get().effectivePlan === PLANS.PRO,
  isFree: () => get().effectivePlan === PLANS.FREE,
  isTrialing: () => get().status === 'TRIALING',

  getTrialDaysRemaining: () => {
    const { trialDaysRemaining, trialEndsAt } = get()
    if (typeof trialDaysRemaining === 'number') return trialDaysRemaining
    if (!trialEndsAt) return null
    const diffDays = Math.ceil((new Date(trialEndsAt) - new Date()) / (1000 * 60 * 60 * 24))
    return diffDays > 0 ? diffDays : 0
  },

  getPlanDisplayName: () => {
    return PLAN_DISPLAY[get().effectivePlan]?.name || 'Free'
  },

  getPlanColor: () => {
    return PLAN_DISPLAY[get().effectivePlan]?.color || PLAN_DISPLAY[PLANS.FREE].color
  },
}))

/**
 * Reactive feature-check hook. Subscribes to features + vehicleLimit + vehicleCount
 * so React re-renders whenever the entitlement changes (e.g. after loadFromBackend
 * runs in an effect). Always read gating through this hook from components, never
 * via store.getState().
 */
export const useFeatures = () => {
  const features = useSubscriptionStore((s) => s.features)
  const vehicleLimit = useSubscriptionStore((s) => s.vehicleLimit)
  const vehicleCount = useSubscriptionStore((s) => s.vehicleCount)
  const has = (f) => features.includes(f)
  return {
    // Vehicle limit
    canAddVehicle: () => vehicleCount < vehicleLimit,
    vehicleLimit,
    vehicleCount,

    // Feature flags — names match the backend PlanFeatureCatalog list.
    canUseExpenses: () => has('expense_tracking'),
    canUseFuel: () => has('fuel_tracking'),
    canUseRepairs: () => has('service_history'),
    canUseServiceHistory: () => has('service_history'),
    canUseDocuments: () => has('document_upload_unlimited'),
    canUseAnalytics: () => has('basic_analytics'),
    canUseReminders: () => has('mot_reminder'),
    canExport: () => has('csv_export'),
    canUseAIScanning: () => has('ai_document_scanning'),
  }
}

export { PLAN_DISPLAY }
